import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  ARTIFACT_RESOURCE_TYPES,
  type ArtifactMetadataView,
  type ArtifactResourceType,
} from "../lib/api";

/**
 * Secure artifact upload/download UI (Phase 8, section 25/18).
 *
 * SAFETY: the browser never supplies an object key or tenant id beyond the path.
 * Uploads go request-upload → PUT to a short-lived signed URL → finalize. Downloads
 * go request-download → open a short-lived signed URL OR stream from the server.
 * Only artifact METADATA is shown (type, size, hash, retention, encryption state);
 * the encryption key is never returned, and no permanent public URL is ever rendered.
 */

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function ArtifactRow({
  artifact,
  onDownload,
  downloading,
}: {
  artifact: ArtifactMetadataView;
  onDownload: (artifact: ArtifactMetadataView) => void;
  downloading: boolean;
}): JSX.Element {
  const deleted = artifact.deletedAtIso !== null;
  return (
    <Card title={artifact.resourceType}>
      <div style={{ display: "grid", gap: "0.35rem" }}>
        <p className="muted">
          Size: <span className="mono">{formatBytes(artifact.size)}</span> · Type:{" "}
          <span className="mono">{artifact.mimeType}</span>
        </p>
        <p className="muted">
          SHA-256: <span className="mono">{artifact.sha256.slice(0, 16)}…</span>
        </p>
        <p className="muted">Retention: {artifact.retentionClass}</p>
        <div className="row" style={{ gap: "0.75rem" }}>
          <StatusIndicator
            tone={artifact.encrypted ? "ok" : "neutral"}
            label={artifact.encrypted ? "Encrypted at rest" : "Not encrypted"}
          />
          <StatusIndicator tone={artifact.immutable ? "ok" : "neutral"} label={artifact.immutable ? "Immutable" : "Mutable"} />
          {artifact.legalHold ? <StatusIndicator tone="warn" label="Legal hold" /> : null}
        </div>
        <p className="muted">
          Created {new Date(artifact.createdAtIso).toUTCString()}
          {artifact.expiresAtIso ? ` · expires ${new Date(artifact.expiresAtIso).toUTCString()}` : ""}
        </p>
        <div className="row">
          <Button variant="secondary" onClick={() => onDownload(artifact)} disabled={deleted || downloading}>
            {downloading ? "Preparing…" : "Download"}
          </Button>
          {deleted ? <span className="muted">Deleted</span> : null}
        </div>
      </div>
    </Card>
  );
}

export function ArtifactsView({
  artifacts,
  onDownload,
  downloadingId,
}: {
  artifacts: ArtifactMetadataView[];
  onDownload: (artifact: ArtifactMetadataView) => void;
  downloadingId: string | null;
}): JSX.Element {
  if (artifacts.length === 0) {
    return <EmptyState title="No artifacts yet">Uploaded and generated artifacts will appear here.</EmptyState>;
  }
  return (
    <div className="card-grid">
      {artifacts.map((artifact) => (
        <ArtifactRow
          key={artifact.id}
          artifact={artifact}
          onDownload={onDownload}
          downloading={downloadingId === artifact.id}
        />
      ))}
    </div>
  );
}

type ListState =
  | { kind: "loading" }
  | { kind: "ready"; data: ArtifactMetadataView[] }
  | { kind: "unavailable"; detail?: string };

export function Artifacts(): JSX.Element {
  const { tenantId, tenantName } = useAppContext();
  const [list, setList] = useState<ListState>({ kind: "loading" });
  const [resourceType, setResourceType] = useState<ArtifactResourceType>("documents");
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setList({ kind: "loading" });
    api.artifacts
      .list(tenantId)
      .then((res) => setList({ kind: "ready", data: res.artifacts }))
      .catch((error: unknown) =>
        setList({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = useCallback(async () => {
    const file = fileRef.current?.files?.[0];
    if (!tenantId || !file) return;
    setUploading(true);
    setNotice(null);
    try {
      const session = await api.artifacts.requestUpload(tenantId, {
        resourceType,
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
      // PUT the bytes straight to the short-lived signed URL (never through the API).
      const put = await fetch(session.upload.url, { method: session.upload.method, body: file });
      if (!put.ok) throw new ApiError(put.status, "upload_failed", "Upload to storage failed.");
      await api.artifacts.finalizeUpload(tenantId, { sessionId: session.sessionId });
      setNotice("Upload complete.");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : "Upload failed. Please retry.");
    } finally {
      setUploading(false);
    }
  }, [tenantId, resourceType, load]);

  const onDownload = useCallback(
    async (artifact: ArtifactMetadataView) => {
      if (!tenantId) return;
      setDownloadingId(artifact.id);
      setNotice(null);
      try {
        const directive = await api.artifacts.requestDownload(tenantId, artifact.id);
        if (directive.mode === "signed_url") {
          window.open(directive.url, "_blank", "noopener,noreferrer");
        } else {
          // Server-mediated stream (encrypted artifacts). Fetch with the session cookie.
          const res = await fetch(api.artifacts.streamUrl(directive.streamPath), { credentials: "include" });
          if (!res.ok) throw new ApiError(res.status, "download_failed", "Download failed.");
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = directive.filename;
          anchor.click();
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        setNotice(error instanceof ApiError ? error.message : "Download failed. Please retry.");
      } finally {
        setDownloadingId(null);
      }
    },
    [tenantId],
  );

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Artifacts" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to manage artifacts.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Artifacts" />
        <EmptyState title="Select a workspace">
          Choose a workspace to view and manage its stored artifacts.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Artifacts">
        <p>
          Securely stored exports and documents for {tenantName ?? "this workspace"}. We show metadata only —
          artifacts are never exposed through a permanent public link.
        </p>
      </PageHeader>

      <Card title="Upload an artifact" description="Uploaded directly to short-lived, server-issued storage.">
        <div className="row" style={{ alignItems: "flex-end" }}>
          <label className="field">
            <span className="field__label">Artifact type</span>
            <select
              className="field__input"
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value as ArtifactResourceType)}
            >
              {ARTIFACT_RESOURCE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">File</span>
            <input className="field__input" ref={fileRef} type="file" aria-label="File to upload" />
          </label>
          <Button variant="primary" onClick={onUpload} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
        {notice ? (
          <p role="status" className="muted" style={{ marginTop: "0.75rem" }}>
            {notice}
          </p>
        ) : null}
      </Card>

      <section style={{ marginTop: "1.5rem" }}>
        <h2>Stored artifacts</h2>
        {list.kind === "loading" ? <LoadingState label="Loading artifacts…" /> : null}
        {list.kind === "unavailable" ? (
          <div className="state" role="alert">
            <ApiUnavailableState detail={list.detail} />
            <Button variant="secondary" onClick={load}>
              Retry
            </Button>
          </div>
        ) : null}
        {list.kind === "ready" ? (
          <ArtifactsView artifacts={list.data} onDownload={onDownload} downloadingId={downloadingId} />
        ) : null}
      </section>
    </>
  );
}
